import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@100mslive/hms-ai-ui";
import {
  ArrowLeft,
  Check,
  CheckCircle,
  DatabaseScriptPlus,
  Download,
  Edit,
  NavArrowDown,
  NavArrowUp,
  Page,
  Phone,
  Table2Columns,
  XmarkCircle,
} from "iconoir-react";
import { debounce, snakeCase } from "lodash-es";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";

import { APIStateEnum } from "../../../common/api/states";
import { Tag } from "../../../common/components/tag";
import {
  createCampaign,
  uploadCSVDataSource,
  uploadExcelDataSource,
  validateCampaignName,
} from "../../../services/campaigns/index";
import {
  fetchHasSampleXLSX,
  fetchJourneys,
  fetchSampleXLSX,
} from "../../../services/journeys";
import { type AppDispatch, type RootState } from "../../../store";
import {
  resetCreateCampaignState,
  setActiveStepIndex,
  setAssetId,
  setCallFlowId,
  setCampaignCSVUploadStatus,
  setCampaignUserData,
  setCSVError,
  setCSVFile,
  setName as setCampaignNameToStore,
} from "../../../store/createCampaign/store";
import type { CallFlow } from "../../../types/journey";
import { ReviewSchema } from "../components/ReviewSchema";
import { Stepper } from "../components/stepper";
import StepperFooter from "../components/StepperFooter";
import { StepperProgress } from "../components/stepperProgress";
import { UploadFile } from "../components/UploadFile";
import {
  convertToCSV,
  createDataFromSchema,
  downloadCSVForData,
} from "../components/validateCSV";
import type { Step } from "../types";

const DownloadDataSource = ({
  journeys,
  callFlowId,
  hasSampleXLSX,
}: {
  journeys: CallFlow[];
  callFlowId: string;
  hasSampleXLSX: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);

  if (!hasSampleXLSX) {
    return (
      <Button
        variant="primary"
        size="sm"
        className="max-w-max"
        onClick={() => {
          const journey =
            journeys.find(journey => journey.id === callFlowId) ||
            ({} as CallFlow);
          const schema = journey?.input_schema?.data_schema || {};
          const data = createDataFromSchema(schema);
          const csv = convertToCSV(data);
          downloadCSVForData(csv);
        }}
      >
        <Flex>
          <p>Download CSV</p>
          <Download className="h-5 w-5 text-inherit ml-2" strokeWidth={2} />
        </Flex>
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary-gray" className="max-w-max flex gap-2 w-48">
          Download Sample
          {open ? (
            <NavArrowUp className="h-5 w-5" strokeWidth={2} />
          ) : (
            <NavArrowDown className="h-5 w-5" strokeWidth={2} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        {hasSampleXLSX && (
          <DropdownMenuItem
            className="w-48 py-3 px-4 flex gap-2 cursor-pointer"
            onClick={() => {
              dispatch(fetchSampleXLSX(callFlowId));
            }}
          >
            <Page className="h-5 w-5 text-utility-teal-200" strokeWidth={2} />
            <div className="flex-1">Sample XLSX</div>
            <Download className="h-5 w-5 text-inherit" strokeWidth={2} />
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="w-48 py-3 px-4 flex gap-2 cursor-pointer"
          onClick={() => {
            const journey =
              journeys.find(journey => journey.id === callFlowId) ||
              ({} as CallFlow);
            const schema = journey?.input_schema?.data_schema || {};
            const data = createDataFromSchema(schema);
            const csv = convertToCSV(data);
            downloadCSVForData(csv);
          }}
        >
          <Page className="h-5 w-5" strokeWidth={2} />
          <div className="flex-1">Sample CSV</div>
          <Download className="h-5 w-5 text-inherit" strokeWidth={2} />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function CreateCampaignsContainer() {
  const dispatch = useDispatch<AppDispatch>();
  const debouncedValidateCampaignName = useMemo(
    () =>
      debounce((name: string) => {
        dispatch(validateCampaignName(name));
      }, 500),
    [dispatch],
  );

  let journey: CallFlow | undefined;
  useEffect(() => {
    dispatch(fetchJourneys());
    return () => {
      dispatch(resetCreateCampaignState());
    };
  }, [dispatch]);

  const navigate = useNavigate();

  const role = useSelector(
    (state: RootState) => state.user.userOganizationDetails.role_name,
  );

  useEffect(() => {
    if (role && role !== "Admin") {
      navigate("/");
    }
  }, [role]);

  const {
    activeStepIndex,
    callFlowId,
    hasSampleXLSX,
    campaignUserData,
    validateCampaign,
    csvError,
    csvFile,
    fileName,
    campaignCSVUploadStatus,
    createCampaignStatus,
    assetId,
    fetchValidateCampaignStatus,
    createdCampaign,
  } = useSelector((state: RootState) => state.createCampaign);
  const [openReviewSchema, setOpenReviewSchema] = useState(false);
  const { journeys, fetchJourneysStatus } = useSelector(
    (state: RootState) => state.journey,
  );
  const [campaignName, setCampaignName] = useState("");

  //redirect to created campaign after creation placed here to stop recreating steps config

  if (createdCampaign?.id) {
    return (
      <Navigate replace to={`/campaign-logs/details/${createdCampaign.id}`} />
    );
  }

  const stepsConfig: Record<string, Step> = {
    SELECT_CALLFLOW: {
      id: 0,
      content: {
        title: "Select Call Flow",
        icon: <Phone className="text-txt-brand-tertiary h-4 w-4 " />,
        description:
          "Choose the call flow which should be used for placing calls. The call flow defines the input, the agent script, expected output and retry rules.",
      },
      input:
        fetchJourneysStatus === APIStateEnum.IN_PROGRESS ? (
          <Skeleton
            count={1}
            containerClassName="flex-1 flex w-full"
            className="rounded-md"
            height={36}
          />
        ) : (
          <Select
            value={callFlowId}
            onValueChange={value => {
              if (journeys.length > 0) {
                journey = (journeys.find(journey => journey.id === value) ||
                  {}) as CallFlow;

                if (journey) {
                  dispatch(setCallFlowId(value));
                  dispatch(fetchHasSampleXLSX(value));
                } else {
                  console.error("Journey not found");
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a Call Flow" />
            </SelectTrigger>
            <SelectContent>
              {journeys.map(journey => (
                <SelectItem
                  key={journey.id}
                  textValue={journey.display_name}
                  value={journey.id}
                  className="cursor-pointer"
                >
                  {journey.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      footer: {
        primaryCTA: {
          text: "Next",
          disabled: callFlowId === "",

          onClick: () => {
            dispatch(setActiveStepIndex(activeStepIndex + 1));
          },
        },
      },
    },
    ADD_DATA_SOURCE: {
      id: 1,

      content: {
        title: "Add Data Source",
        icon: <Edit className="text-txt-brand-tertiary h-4 w-4 " />,
        description: (
          <p className="text-txt-tertiary text-sm">
            Your file should include the fields that are accepted and needed for
            selected call flow in addition to phone number(s).&nbsp;
            {/* <span
              className="hover:underline hover:text-txt-brand-tertiary text-txt-brand-secondary hover:cursor-pointer"
              onClick={() => {
                const journey =
                  journeys.find(journey => journey.id === callFlowId) ||
                  ({} as CallFlow);
                const schema = journey?.input_schema?.data_schema || {};
                const data = createDataFromSchema(schema);
                const csv = convertToCSV(data);
                downloadCSVForData(csv);
              }}
            >
              Download a sample CSV
            </span> */}
          </p>
        ),
      },
      input: (
        <UploadFile
          setAssetId={(str: string) => {
            dispatch(setAssetId(str));
          }}
          resetStatus={() => dispatch(setCampaignCSVUploadStatus(undefined))}
          data={campaignUserData}
          setData={data => {
            dispatch(setCampaignUserData(data));
          }}
          campaignCSVUploadStatus={campaignCSVUploadStatus}
          csvFile={csvFile}
          validationError={csvError || ""}
          callFlow={
            journeys.find(journey => journey.id === callFlowId) ||
            ({} as CallFlow)
          }
          setFileData={function (data: any[]): void {
            dispatch(setCampaignUserData(data));
          }}
          setFile={function (file?: File): void {
            dispatch(setCSVFile(file));
          }}
          setValidationError={function (error: string): void {
            dispatch(setCSVError(error));
          }}
        />
      ),
      preview: (
        <>
          <Flex direction="column" className="gap-3">
            <Flex className="justify-between items-center">
              <Flex className="gap-3 items-center">
                <div
                  className=" p-2 rounded-lg bg-bg-brand-primary border border-bd-secondary"
                  style={{
                    boxShadow: `
      0px 1px 2px 0px #0A0D120D,
      inset 0px -2px 0px 0px #0A0D120D,
      inset 0px 0px 0px 1px #0A0D122E
    `,
                  }}
                >
                  <Table2Columns className="text-txt-brand-tertiary h-4 w-4 " />
                </div>
                <p className="text-xl font-medium">Before you upload</p>
              </Flex>
            </Flex>
            <p className="text-txt-tertiary text-sm">
              Ensure your data source contains all required fields. The column
              names should match exactly as shown in the field guide. You can
              download a template to get started.{" "}
              <span
                className="text-txt-brand-tertiary font-bold hover:underline cursor-pointer"
                onClick={() => {
                  setOpenReviewSchema(true);
                }}
              >
                View Field Guide &gt;
              </span>
            </p>
            <DownloadDataSource
              journeys={journeys}
              callFlowId={callFlowId}
              hasSampleXLSX={hasSampleXLSX}
            />
            <ReviewSchema
              open={openReviewSchema}
              setOpen={setOpenReviewSchema}
              callFlow={
                journeys.find(journey => journey.id === callFlowId) ||
                ({} as CallFlow)
              }
            >
              <DownloadDataSource
                journeys={journeys}
                callFlowId={callFlowId}
                hasSampleXLSX={hasSampleXLSX}
              />
            </ReviewSchema>
          </Flex>
        </>
      ),

      footer: {
        primaryCTA: {
          disabled: assetId
            ? campaignCSVUploadStatus !== APIStateEnum.SUCCESS
            : campaignCSVUploadStatus === APIStateEnum.IN_PROGRESS ||
              !csvFile ||
              csvError,
          text: assetId ? "Next" : "Upload",
          onClick: () => {
            if (csvError) {
              return;
            }
            const isExcel =
              fileName?.includes("xlsx") || fileName?.includes("xls");
            if (assetId) {
              let substrFile;
              if (isExcel) {
                substrFile = fileName?.split(".xls")[0];
              } else {
                substrFile = fileName?.split(".csv")[0];
              }

              const callflowName = journeys.find(
                journey => journey.id === callFlowId,
              )?.display_name;
              const defaultCampaignName = `${snakeCase(
                callflowName,
              )}-${snakeCase(substrFile)}-${moment().format("DDMMMYYYY-HHmmss")}`;
              setCampaignName(defaultCampaignName);
              dispatch(validateCampaignName(defaultCampaignName));
              dispatch(setActiveStepIndex(activeStepIndex + 1));
              return;
            }
            if (fileName?.includes("xlsx") || fileName?.includes("xls")) {
              dispatch(uploadExcelDataSource(csvFile as File));
            } else {
              dispatch(uploadCSVDataSource(csvFile as File));
            }
          },
        },
        secondaryCTA: {
          text: "Previous",
          onClick: () => {
            if (activeStepIndex < steps.length - 1) {
              dispatch(setActiveStepIndex(activeStepIndex - 1));
            }
          },
        },
      },
    },

    NAME_CAMPAIGN: {
      id: 2,
      content: {
        title: "Name Campaign",
        icon: (
          <DatabaseScriptPlus className="text-txt-brand-tertiary h-4 w-4 " />
        ),
        description:
          "Name your campaign as per your convention for easier identification.",
      },
      input: (() => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setCampaignName(value);
          debouncedValidateCampaignName(value.trim());
        };

        return (
          <Flex direction="column" className="gap-3 w-full">
            <Input
              type="text"
              value={campaignName}
              onChange={handleChange}
              className="w-full"
            />

            {campaignName === "" ? (
              <Flex className="gap-2">
                <XmarkCircle className="h-4 w-4 text-txt-error-primary self-center" />
                <p className="text-txt-tertiary text-sm">
                  Campaign name can&apos;t be empty.
                </p>
              </Flex>
            ) : !validateCampaign ? (
              <Flex className="gap-2">
                <XmarkCircle className="h-4 w-4 text-txt-error-primary self-center" />
                <p className="text-txt-tertiary text-sm">
                  Campaign name is not available. Please try another name.
                </p>
              </Flex>
            ) : (
              <Flex className="gap-2">
                <CheckCircle className="h-4 w-4 text-txt-success-primary self-center" />
                <p className="text-txt-tertiary text-sm">
                  Campaign name is available.
                </p>
              </Flex>
            )}
          </Flex>
        );
      })(),
      preview: (
        <>
          <Flex direction="column" className="gap-3">
            <Flex className="justify-between items-center">
              <Flex className="gap-3 items-center">
                <div
                  className=" p-2 rounded-lg bg-bg-brand-primary border border-bd-secondary"
                  style={{
                    boxShadow: `
      0px 1px 2px 0px #0A0D120D,
      inset 0px -2px 0px 0px #0A0D120D,
      inset 0px 0px 0px 1px #0A0D122E
    `,
                  }}
                >
                  {" "}
                  <Phone className="text-txt-brand-tertiary h-4 w-4 " />
                </div>
                <p className="text-xl font-medium">Call Flow</p>
              </Flex>
              <Tag
                className="font-medium"
                value="Selected"
                txtColor="text-utility-success-700"
                bgColor="bg-utility-success-50"
                bdColor="border border-utility-success-200"
                showIcon={true}
                icon={<Check className="h-4 w-4 text-utility-success-700" />}
              />
            </Flex>
            <Input
              disabled={true}
              value={(() => {
                const selectedJourney = journeys.find(
                  journey => journey.id === callFlowId,
                );
                return selectedJourney ? selectedJourney.display_name : "";
              })()}
            />
          </Flex>
          <Flex direction="column" className="gap-3">
            <Flex className="justify-between items-center">
              <Flex className="gap-3 items-center">
                <div
                  className=" p-2 rounded-lg bg-bg-brand-primary border border-bd-secondary"
                  style={{
                    boxShadow: `
      0px 1px 2px 0px #0A0D120D,
      inset 0px -2px 0px 0px #0A0D120D,
      inset 0px 0px 0px 1px #0A0D122E
    `,
                  }}
                >
                  {" "}
                  <DatabaseScriptPlus className="text-txt-brand-tertiary h-4 w-4 " />
                </div>
                <p className="text-xl font-medium">Data Source</p>
              </Flex>
              <Tag
                value="Added"
                txtColor="text-utility-success-700"
                bgColor="bg-utility-success-50"
                bdColor="border border-utility-success-200"
                showIcon={true}
                icon={<Check className="h-4 w-4 text-utility-success-700" />}
              />
            </Flex>
            <Input disabled={true} value={fileName} />
          </Flex>
        </>
      ),

      footer: {
        primaryCTA: {
          text: "Start Campaign",
          loading: createCampaignStatus === APIStateEnum.IN_PROGRESS,
          disabled:
            !validateCampaign ||
            fetchValidateCampaignStatus !== APIStateEnum.SUCCESS ||
            createCampaignStatus === APIStateEnum.IN_PROGRESS,
          onClick: () => {
            dispatch(setCampaignNameToStore(campaignName));
            dispatch(createCampaign());
          },
        },
        secondaryCTA: {
          text: "Previous",
          onClick: () => {
            dispatch(setActiveStepIndex(activeStepIndex - 1));
          },
        },
      },
    },
  };
  const steps: Step[] = Object.values(stepsConfig).sort((a, b) => a.id - b.id);

  return (
    <Flex
      className="bg-bg-tertiary h-full w-full rounded-2xl relative"
      direction="column"
    >
      <Flex
        justify="between"
        className="border-b border-bd-secondary p-4  bg-bg-primary w-full rounded-t-2xl items-center"
      >
        <Button
          variant="secondary-gray"
          onClick={() => window.history.back()}
          size="sm"
          className="p-2"
        >
          <ArrowLeft height={20} width={20} className="text-inherit" />
        </Button>
        <StepperProgress steps={steps} activeStepIndex={activeStepIndex} />
        <div className="h-8 w-8"></div>
      </Flex>
      <Box className="rounded-br-2xl rounded-bl-2xl bg-bg-primary w-full flex-1 flex items-center justify-center pt-8 pb-[72px] px-6">
        <Stepper step={steps[activeStepIndex]} />
      </Box>
      <div className="absolute bottom-0 right-0 bg-bg-secondary-hover w-full box-border px-6 py-4 rounded-b-xl shadow border-t border-bd-primary">
        <StepperFooter footer={steps[activeStepIndex]?.footer} steps={steps} />
      </div>
    </Flex>
  );
}
